import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { DataLayer } from '@grandvisionhq/analytics';
import { useMutation } from '@grandvisionhq/graphql';
import { useCartStorage, CART_STORAGE_KEY } from '@grandvisionhq/lib-utils-cart';
import { useIntl, useApiConfig, useFeatureToggles } from '@grandvisionhq/state';
import { monetate } from '@grandvisionhq/svc-monetate';
import { useDispatchTrackEvent, useDispatchTrackErrorEvent } from '@grandvisionhq/svc-tealium/events';
import { isNotNull } from '@grandvisionhq/utils';
import '../context/Cart/context.js';
import '@grandvisionhq/common';
import { useCartSettings } from '../context/CartSettings/context.js';
import '../models/add-to-cart/add-to-cart.js';
import '../models/cart/cart.js';
import '../models/subscription/subscription-details.js';
import '../models/checkoutState/checkoutState.js';
import { appendSanitizationItems } from '../utils/append-sanitization-items.js';
import '../utils/cart-count-var.js';
import '../utils/cart-id-var.js';
import { getAddedLineItems } from '../utils/get-added-line-items.js';
import '@grandvisionhq/commercetools';
import { getSolvableValidationErrors } from '../utils/get-solvable-validation-errors.js';
import { getValidationErrorCodes } from '../utils/get-validation-error-codes.js';
import '../utils/get-unrecoverable-validation-errors.js';
import { lineItemDataMapper } from '../utils/line-item-data-mapper.js';
import '../utils/get-failure-error-codes.js';
import '../utils/get-validation-errors-by-line-item.js';
import { toVisualLineItem } from '../mappers/view/visual-line-item/cart-line-item.js';
import '../operations/fragments/cart-details.js';
import '../operations/fragments/cart-validation-errors.js';
import '../operations/fragments/recover-inactive-cart-failure.js';
import '../operations/fragments/recover-inactive-cart-result.js';
import { ADD_CART_LINE_ITEMS } from '../operations/mutations/add-cart-line-items.js';
import '../operations/mutations/add-cart-voucher-codes.js';
import { CREATE_CART } from '../operations/mutations/create-cart.js';
import '../operations/mutations/create-cart-from-instant-checkout-link.js';
import '../operations/mutations/create-cart-from-order.js';
import '../operations/mutations/duplicate-cart-line-items.js';
import '../operations/mutations/remove-cart-voucher-codes.js';
import '../operations/mutations/remove-cart-line-items.js';
import '../operations/mutations/update-cart-line-items.js';
import '../operations/mutations/replace-line-item-contact-lenses-bundle-benelux.js';
import '../operations/mutations/recover-inactive-cart.js';
import '../operations/queries/get-cart.js';
import '../operations/queries/get-total-line-items.js';
import '../operations/queries/get-cart-estimated-delivery.js';
import { getCartRows } from '../mappers/analytics/monetate/get-cart-rows.js';
import { mapLineItemToDataLayerEvent } from '../mappers/analytics/data-layer/line-item-to-data-layer-event.js';
import '@grandvisionhq/payments';
import { mapVisualLineItemProductToTealiumProduct } from '../mappers/analytics/tealium/line-item-to-tealium-product.js';
import { errorsMapping } from '../mappers/error-codes.js';
import { mapDraftLineItemToServer } from '../mappers/server/line-item.js';
import { dispatchMiniCartEvent } from '../modules/MiniCart/utils/event.js';

const useAddToCart = ()=>{
    const intl = useIntl();
    const { locale , currency , country  } = intl;
    const settings = useCartSettings();
    const { apiUrl  } = useApiConfig();
    const [cart, { setData  }] = useCartStorage();
    const { isMonetateProductDataEnabled  } = useFeatureToggles();
    const dispatchTrackEvent = useDispatchTrackEvent();
    const dispatchTrackErrorEvent = useDispatchTrackErrorEvent();
    const [createCart, createCartMutation] = useMutation(CREATE_CART);
    const [addCartLineItems, addCartLineItemsMutation] = useMutation(ADD_CART_LINE_ITEMS);
    const handleTrackErrorTealium = React.useCallback((errorDetails, errorMessage, errorSource)=>dispatchTrackErrorEvent(()=>Promise.resolve(swcHelpers.objectSpread({
                Error_Source: errorSource || 'Server',
                Error_Code: 'Add to cart error',
                Error_Details: errorDetails
            }, errorMessage ? {
                Error_Message: errorMessage
            } : {}))
        )
    , [
        dispatchTrackErrorEvent
    ]);
    const handleOnCompleted = React.useCallback((addedLineItemDrafts, data, onError, onSuccess, onValidationError, onSolvableValidationError, pushRemoveLineItemDataLayerEvent, isQuantityDiscountSelected)=>{
        let result;
        if ('addCartLineItems' in data) {
            result = data.addCartLineItems;
        }
        if ('createCart' in data) {
            result = data.createCart;
        }
        if (result.validationErrors) {
            const solvableValidationErrors = getSolvableValidationErrors(result.validationErrors);
            if (solvableValidationErrors.length > 0 && onSolvableValidationError) {
                onSolvableValidationError(solvableValidationErrors);
                return;
            }
            const validationErrors = getValidationErrorCodes(result.validationErrors);
            onValidationError && onValidationError(validationErrors);
            if (!result.success) {
                return;
            }
        }
        if (!result.cart) {
            handleTrackErrorTealium(errorsMapping.CART_IS_EMPTY);
            return onError(errorsMapping.CART_IS_EMPTY);
        }
        const { cartHash  } = result;
        const { id , numberOfItems , lineItems  } = result.cart;
        setData({
            cartId: id,
            cartHash: cartHash || (cart === null || cart === void 0 ? void 0 : cart.cartHash) || null,
            cartCount: numberOfItems
        });
        const addedLineItems = getAddedLineItems(lineItems, addedLineItemDrafts);
        const addedLineItemsMapped = lineItemDataMapper(addedLineItems, settings, intl);
        const addedVisualLineItems = addedLineItems.map((lineItem)=>toVisualLineItem(lineItem, intl, settings, apiUrl)
        ).filter((lineItem)=>Boolean(lineItem)
        );
        const dataLayerEvents = mapLineItemToDataLayerEvent(DataLayer.DataLayerTypes.EventType.add, addedLineItemsMapped, id, isQuantityDiscountSelected);
        const products = mapVisualLineItemProductToTealiumProduct(addedVisualLineItems, true);
        dispatchTrackEvent(()=>Promise.resolve({
                id: 'Event',
                Events_CartAdd: '1',
                Products: products
            })
        );
        dataLayerEvents && DataLayer.pushEcommerceEvent(dataLayerEvents);
        pushRemoveLineItemDataLayerEvent && pushRemoveLineItemDataLayerEvent();
        dispatchMiniCartEvent('added', addedVisualLineItems);
        onSuccess && onSuccess(addedLineItemsMapped);
        monetate().trackCartContents(isMonetateProductDataEnabled ? getCartRows(lineItems, currency) : []);
    }, [
        apiUrl,
        cart === null || cart === void 0 ? void 0 : cart.cartHash,
        currency,
        dispatchTrackEvent,
        handleTrackErrorTealium,
        intl,
        isMonetateProductDataEnabled,
        setData,
        settings, 
    ]);
    const addToCart = React.useCallback(function() {
        var _ref = swcHelpers.asyncToGenerator(function*(lineItemDrafts, onSuccess, onValidationError, onError = ()=>undefined
        , onSolvableValidationError, sanitizationItems, pushRemoveLineItemDataLayerEvent, isQuantityDiscountSelected, options = {}) {
            let lineItems = lineItemDrafts.map(mapDraftLineItemToServer).filter(isNotNull);
            if (!lineItems.length) {
                handleTrackErrorTealium('No lineItems found', errorsMapping.SOMETHING_WENT_WRONG);
                return onError(errorsMapping.SOMETHING_WENT_WRONG);
            }
            if (sanitizationItems) {
                lineItems = appendSanitizationItems(lineItems, sanitizationItems);
            }
            /**
       * On the PDP, there will be multiple instances of this hook (useAddToCart)
       * running. This is causing a mismatch in the StoredCart data value that is returned
       * from localStorage. Pending rethinking how to fix this issue properly, we'll
       * let this hook itself get the StoredCart value (containing cartId) from localStorage
       * instead of relying on the useCartStorage hook (which might return an old StoredCart value).
       */ let localCartData = null;
            try {
                const localStorageCartData = window.localStorage.getItem(CART_STORAGE_KEY);
                localCartData = isNotNull(localStorageCartData) ? JSON.parse(localStorageCartData) : null;
            } catch (err) {
                const errorMessage = `Failed retrieving cart data from localStorage. Error: ${err}`;
                handleTrackErrorTealium(errorMessage, undefined, 'Client');
                console.error(errorMessage);
            }
            const { prescriptionSignatures  } = options;
            try {
                const { data , errors  } = yield isNotNull(localCartData) && isNotNull(localCartData.cartId) ? addCartLineItems({
                    variables: {
                        cartInput: {
                            cartId: localCartData.cartId,
                            lineItems
                        }
                    },
                    context: {
                        forwardHeaders: {
                            'cart-hash': cart.cartHash
                        }
                    }
                }) : createCart({
                    variables: {
                        cartInput: {
                            locale,
                            currency,
                            country,
                            lineItems,
                            prescriptionSignatures
                        }
                    }
                });
                if (errors) {
                    handleTrackErrorTealium(errorsMapping.SOMETHING_WENT_WRONG);
                    onError(errorsMapping.SOMETHING_WENT_WRONG);
                }
                if (data) {
                    handleOnCompleted(lineItemDrafts, data, onError, onSuccess, onValidationError, onSolvableValidationError, pushRemoveLineItemDataLayerEvent, isQuantityDiscountSelected);
                }
            } catch (error) {
                const errorMessage = `Failed adding line items to cart. Error: ${error}`;
                console.error(errorMessage);
                handleTrackErrorTealium(errorsMapping.SOMETHING_WENT_WRONG, errorMessage);
                onError(errorsMapping.SOMETHING_WENT_WRONG);
            }
        });
        return function(lineItemDrafts, onSuccess, onValidationError) {
            return _ref.apply(this, arguments);
        };
    }(), [
        addCartLineItems,
        cart.cartHash,
        country,
        createCart,
        currency,
        handleOnCompleted,
        handleTrackErrorTealium,
        locale, 
    ]);
    const { called , loading , error: error1 , data: data1  } = cart.cartId ? addCartLineItemsMutation : createCartMutation;
    let newCart;
    if (data1 && 'createCart' in data1) {
        newCart = data1.createCart.cart;
    }
    if (data1 && 'addCartLineItems' in data1) {
        newCart = data1.addCartLineItems.cart;
    }
    return [
        addToCart,
        {
            called,
            loading,
            error: error1,
            cart: newCart
        }
    ];
};

export { useAddToCart };
//# sourceMappingURL=use-add-to-cart.js.map
